import { zodResolver } from '@hookform/resolvers/zod';
import { toBioHtml } from '@onesource/schemas';
import {
  useGetCitiesQuery,
  useGetCountriesQuery,
  useGetExpertiseQuery,
  useGetProfessionsQuery,
  useGetStatesQuery,
} from '@onesource/services';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import {
  EMPTY_FORM_VALUES,
  getFieldError,
  getMissingFields,
  getMissingFieldsWithStep,
  getValidatedFieldStep,
  isFormDirty,
  isStepComplete,
  ProFormSchema,
  STEP_FIELDS,
  STEPS,
  TFormIssue,
  TProDirectoryAccountProfile,
  TProFormValues,
  VALIDATED_FIELD_LABELS,
} from './steps';

const EMPTY_ACCOUNT_PROFILE: TProDirectoryAccountProfile = {
  fullName: '',
  bio: '',
  phone: '',
  avatar: '',
  socials: { facebook: '', instagram: '', youtube: '', twitter: '', linkedin: '', tiktok: '' },
};

type TOptions = {
  /** Values the form is hydrated from; `null` while still loading. */
  baselineValues: TProFormValues | null;
  /** The member's own profile — enables "Copy from My Account". Omit when an admin edits someone else's listing. */
  accountProfile?: TProDirectoryAccountProfile;
};

/**
 * Form state for the Pro Directory profile form, shared by the member's account tab
 * (members-fe) and the admin listing editor (dashboard).
 */
export const useProDirectoryForm = ({ baselineValues, accountProfile }: TOptions) => {
  const profile = accountProfile ?? EMPTY_ACCOUNT_PROFILE;
  const [activeStep, setActiveStep] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [copyFullName, setCopyFullName] = useState(false);
  const [copyBio, setCopyBio] = useState(false);
  const [copyPhone, setCopyPhone] = useState(false);
  const [copySocials, setCopySocials] = useState(false);

  const methods = useForm<TProFormValues>({
    resolver: zodResolver(ProFormSchema),
    defaultValues: EMPTY_FORM_VALUES,
    mode: 'all',
  });

  const {
    reset,
    setValue,
    getValues,
    trigger,
    control,
    formState: { errors },
  } = methods;

  const watchedValues = useWatch({ control });
  const watchedCountryId = useWatch({ control, name: 'businessCountryId' });
  const watchedStateId = useWatch({ control, name: 'businessStateId' });
  const values = watchedValues as Partial<TProFormValues>;

  const missingFields = getMissingFields(values);
  const isComplete = missingFields.length === 0;

  const fullNameMatchesProfile = !!profile.fullName && watchedValues.fullName === profile.fullName;
  const bioMatchesProfile = !!profile.bio && watchedValues.bio === toBioHtml(profile.bio);
  const phoneMatchesProfile = !!profile.phone && watchedValues.phone === profile.phone;
  const socialsMatchProfile =
    !!Object.values(profile.socials).some(Boolean) &&
    watchedValues.socials?.facebook === profile.socials.facebook &&
    watchedValues.socials?.instagram === profile.socials.instagram &&
    watchedValues.socials?.youtube === profile.socials.youtube &&
    watchedValues.socials?.twitter === profile.socials.twitter &&
    watchedValues.socials?.linkedin === profile.socials.linkedin &&
    watchedValues.socials?.tiktok === profile.socials.tiktok;

  // Disable copy checkbox only when: no profile data, OR values already match but copy is NOT active
  // When copy IS active (checked), always allow unchecking
  const disableCopyFullName = !profile.fullName || (!copyFullName && fullNameMatchesProfile);
  const disableCopyBio = !profile.bio || (!copyBio && bioMatchesProfile);
  const disableCopyPhone = !profile.phone || (!copyPhone && phoneMatchesProfile);
  const disableCopySocials = !Object.values(profile.socials).some(Boolean) || (!copySocials && socialsMatchProfile);

  const { data: countries = [] } = useGetCountriesQuery();
  const { data: states = [] } = useGetStatesQuery({ countryId: watchedCountryId });
  const { data: cities = [] } = useGetCitiesQuery({ stateId: watchedStateId });
  const { data: professions = [] } = useGetProfessionsQuery();
  const { data: expertiseItems = [] } = useGetExpertiseQuery();

  const prevCountryIdRef = useRef<number | null>(null);

  useEffect(() => {
    const prevId = prevCountryIdRef.current;
    prevCountryIdRef.current = watchedCountryId ?? null;
    if (!watchedCountryId || watchedCountryId === prevId) {
      return;
    }
    const currentPhone = getValues('phone') ?? '';
    // Only set code if phone is empty or contains only a country code (no subscriber number)
    const isCodeOnly = /^\+\d{1,4}\s*$/.test(currentPhone.trim()) || currentPhone.trim() === '';
    if (!isCodeOnly) {
      return;
    }
    const country = countries.find(c => c.id === watchedCountryId);
    if (country?.phonecode) {
      setValue('phone', `+${country.phonecode}`);
    }
  }, [watchedCountryId, countries, setValue, getValues]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real dirty check: diff current values against the loaded baseline so that
  // editing a field and reverting it clears "dirty" (RHF's isDirty can stick).
  const isActuallyDirty = isFormDirty(values, baselineValues ?? EMPTY_FORM_VALUES);

  useEffect(() => {
    if (baselineValues) {
      reset(baselineValues);
    }
  }, [baselineValues, reset]);

  useEffect(() => {
    if (copyFullName) {
      setValue('fullName', profile.fullName, { shouldDirty: true });
    }
  }, [copyFullName, profile.fullName, setValue]);

  useEffect(() => {
    if (copyBio) {
      setValue('bio', toBioHtml(profile.bio), { shouldDirty: true });
    }
  }, [copyBio, profile.bio, setValue]);

  useEffect(() => {
    if (copyPhone) {
      setValue('phone', profile.phone, { shouldDirty: true });
    }
  }, [copyPhone, profile.phone, setValue]);

  useEffect(() => {
    if (copySocials) {
      setValue('socials.facebook', profile.socials.facebook, { shouldDirty: true });
      setValue('socials.instagram', profile.socials.instagram, { shouldDirty: true });
      setValue('socials.youtube', profile.socials.youtube, { shouldDirty: true });
      setValue('socials.twitter', profile.socials.twitter, { shouldDirty: true });
      setValue('socials.linkedin', profile.socials.linkedin, { shouldDirty: true });
      setValue('socials.tiktok', profile.socials.tiktok, { shouldDirty: true });
    }
  }, [copySocials, profile.socials, setValue]);

  // Scroll step body back to top on step change
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [activeStep]);

  // Step completion: required fields filled + no validation errors within the step's fields
  const hasStepErrors = (step: number): boolean =>
    STEP_FIELDS[step].some(path => {
      let node: unknown = errors;
      for (const part of path.split('.')) {
        if (!node || typeof node !== 'object') {
          return false;
        }
        node = (node as Record<string, unknown>)[part];
      }
      return !!node;
    });

  // Recomputed on every keystroke (useWatch above subscribes to all fields), so
  // memoise on the step-completion booleans rather than the array identity —
  // otherwise the memoised header re-renders on every character typed.
  const baseCompleted = [0, 1, 2].map(step => isStepComplete(values, step) && !hasStepErrors(step));
  const [step0Complete, step1Complete, step2Complete] = baseCompleted;
  const completedSteps = useMemo(
    () => [step0Complete, step1Complete, step2Complete, step0Complete && step1Complete && step2Complete],
    [step0Complete, step1Complete, step2Complete],
  );

  const isStepEnabled = useCallback((_step: number): boolean => true, []);

  const handleStepClick = useCallback(
    (step: number) => {
      if (isStepEnabled(step)) {
        setActiveStep(step);
      }
    },
    [isStepEnabled],
  );

  const handleContinue = useCallback(async () => {
    await trigger(STEP_FIELDS[activeStep]);
    setActiveStep(step => Math.min(step + 1, STEPS.length - 1));
  }, [trigger, activeStep]);

  const handleBack = useCallback(() => setActiveStep(step => Math.max(step - 1, 0)), []);

  // Format errors (e.g. a malformed Business Website) on fields that aren't
  // required — surfaced separately so they read as "fix this" rather than
  // "this is required", and so they don't block Continue/Submit like a missing
  // required field does.
  //
  // Recomputed every render (not memoised on `errors`) — RHF mutates the
  // `formState.errors` object in place for perf, so its reference doesn't
  // change when a field's error clears, and a `useMemo` keyed on it would show
  // stale messages after the user fixes the field.
  const validationIssues = (Object.keys(VALIDATED_FIELD_LABELS) as Array<keyof typeof VALIDATED_FIELD_LABELS>)
    .map(path => ({ path, message: getFieldError(errors, path)?.message }))
    .filter((issue): issue is { path: keyof typeof VALIDATED_FIELD_LABELS; message: string } => !!issue.message);

  // Every outstanding issue across all steps (not just the active one), for the
  // always-visible help popover next to Continue/Preview/Submit.
  const allIssues: TFormIssue[] = [
    ...getMissingFieldsWithStep(values).map(
      ({ label, step }): TFormIssue => ({ kind: 'missing', message: label, step }),
    ),
    ...validationIssues.map(
      ({ path, message }): TFormIssue => ({ kind: 'invalid', message, step: getValidatedFieldStep(path) }),
    ),
  ];

  return {
    methods,
    scrollRef,
    activeStep,
    setActiveStep,
    completedSteps,
    isStepEnabled,
    handleStepClick,
    handleContinue,
    handleBack,
    isComplete,
    isActuallyDirty,
    allIssues,
    countries,
    states,
    cities,
    professions,
    expertiseItems,
    showCopyFromAccount: !!accountProfile,
    profile,
    copy: {
      copyFullName,
      copyBio,
      copyPhone,
      copySocials,
      setCopyFullName,
      setCopyBio,
      setCopyPhone,
      setCopySocials,
      disableCopyFullName,
      disableCopyBio,
      disableCopyPhone,
      disableCopySocials,
    },
  };
};

export type TProDirectoryFormState = ReturnType<typeof useProDirectoryForm>;
